import { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { useNF } from "@/context/NFContext";
import { api, ApiError } from "@/lib/api";
import type { NotaFiscal } from "@/data/mock";
import { RefreshCw } from "lucide-react";
import { toast } from "sonner";

export const PODE_REPROCESSAR = ["Pendente", "Erro OCR", "Recusada", "Aprovada"];
const RATE_LIMIT_MS = 60_000;
const REFRESH_DELAY_MS = 5_000;

export const ReprocessarNotaButton = ({ nf }: { nf: NotaFiscal }) => {
  const { user } = useAuth();
  const { refresh } = useNF();
  const [loading, setLoading] = useState(false);
  const [blockedUntil, setBlockedUntil] = useState<number | null>(null);
  const blockTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => () => {
    if (blockTimerRef.current) clearTimeout(blockTimerRef.current);
  }, []);

  if (!user) return null;
  const podeVer = user.role === "rh" || nf.colaboradorId === user.id;
  if (!podeVer || !PODE_REPROCESSAR.includes(nf.status)) return null;
  if (nf.status === "Aprovada" && user.role !== "rh") return null;

  const bloquearPor = (ms: number) => {
    if (blockTimerRef.current) clearTimeout(blockTimerRef.current);
    setBlockedUntil(Date.now() + ms);
    blockTimerRef.current = setTimeout(() => setBlockedUntil(null), ms);
  };

  const handleReprocessar = async () => {
    setLoading(true);
    try {
      await api.post(`/nfs/${nf.id}/retry-ocr`, {});
      toast.success("Reprocessamento iniciado");
      bloquearPor(RATE_LIMIT_MS);
      setTimeout(() => refresh(), REFRESH_DELAY_MS);
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.status === 403) {
          toast.error("Você não tem acesso a esta NF");
        } else if (err.status === 429) {
          const msRemaining = (err.body as { msRemaining?: number })?.msRemaining ?? RATE_LIMIT_MS;
          toast.error(`Aguarde ${Math.ceil(msRemaining / 1000)}s para tentar novamente`);
          bloquearPor(msRemaining);
        } else {
          toast.error(err.message);
        }
      } else {
        toast.error(err instanceof Error ? err.message : "Erro ao reprocessar nota");
      }
    } finally {
      setLoading(false);
    }
  };

  const bloqueado = blockedUntil !== null && Date.now() < blockedUntil;

  return (
    <Button
      variant="outline"
      className="border-amber-500/40 text-amber-700 hover:bg-amber-500/10 hover:text-amber-800 dark:text-amber-400"
      onClick={handleReprocessar}
      disabled={loading || bloqueado}
    >
      <RefreshCw className="mr-1 h-4 w-4" />
      {loading ? "Reprocessando..." : "Reprocessar Nota"}
    </Button>
  );
};
